import { useMemo, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { EVENT_COLORS } from "../lib/permissions";
import EventPicker from "./EventPicker";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Mail, Send, Users } from "lucide-react";

export default function CommsView({ events, users, me }) {
  const [eventId, setEventId] = useState("");
  const [audience, setAudience] = useState("all");
  const [note, setNote] = useState("");
  const [sendingBlast, setSendingBlast] = useState(false);

  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [sendingPersonal, setSendingPersonal] = useState(false);

  const approvedEvents = useMemo(() => events.filter(e => e.status === "approved"), [events]);
  const ev = approvedEvents.find(e => e.id === eventId);

  const blastCount = useMemo(() => {
    if (!ev) return 0;
    if (audience === "going") return Object.values(ev.rsvps || {}).filter(r => r.status === "going").length;
    if (audience === "targets" && ev.target_user_ids?.length) return ev.target_user_ids.length;
    return users.filter(u => !(u.role === "candidate" && ["Initiation", "Brothers Only"].includes(ev.event_type))).length;
  }, [ev, audience, users]);

  const sendBlast = async () => {
    if (!eventId) { toast.error("Pick an event"); return; }
    setSendingBlast(true);
    try {
      const { data } = await api.post("/comms/send", {
        mode: "event", event_id: eventId, audience, note: note || undefined,
      });
      toast.success(`Reminder sent to ${data.sent} member${data.sent === 1 ? "" : "s"}${data.failed ? ` (${data.failed} failed)` : ""}`);
      setNote("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setSendingBlast(false); }
  };

  const sendPersonal = async () => {
    if (!selected.length) { toast.error("Pick at least one member"); return; }
    if (!message.trim()) { toast.error("Write a message"); return; }
    setSendingPersonal(true);
    try {
      const { data } = await api.post("/comms/send", {
        mode: "personal", user_ids: selected, message,
      });
      toast.success(`Email sent to ${data.sent} member${data.sent === 1 ? "" : "s"}${data.failed ? ` (${data.failed} failed)` : ""}`);
      setMessage(""); setSelected([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setSendingPersonal(false); }
  };

  const toggle = (id, checked) =>
    setSelected(prev => checked ? [...prev, id] : prev.filter(x => x !== id));

  return (
    <div className="space-y-6" data-testid="comms-view">
      <div>
        <h2 className="font-display text-2xl text-[var(--sn-gold)] flex items-center gap-2">
          <Megaphone className="w-6 h-6" /> Chapter Messages
        </h2>
        <p className="text-neutral-500 text-sm">Email the chapter about any event, on any day — or message individual brothers directly.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Event reminder blast */}
        <Card className="glass-card p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
            <Mail className="w-4 h-4 text-[var(--sn-gold)]" /> Event Reminder Blast
          </div>
          <div className="space-y-2">
            <Label>Event</Label>
            <EventPicker events={approvedEvents} value={eventId} onChange={setEventId}
              testid="blast-event-select" placeholder="Search events…" />
          </div>
          {ev && (
            <Badge className={EVENT_COLORS[ev.event_type]?.chip}>{ev.event_type}</Badge>
          )}
          <div className="space-y-2">
            <Label>Send to</Label>
            <div className="flex gap-2 flex-wrap">
              {[["all", "Entire chapter"], ["targets", "Targeted members"], ["going", "RSVP: Going"]].map(([k, label]) => (
                <button key={k} type="button" data-testid={`blast-audience-${k}`}
                  onClick={() => setAudience(k)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    audience === k
                      ? "bg-[var(--sn-gold)] text-black border-[var(--sn-gold)] font-semibold"
                      : "bg-neutral-900 text-neutral-400 border-neutral-700 hover:border-[var(--sn-gold)]/50"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
            <p className="text-xs text-neutral-500 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> {blastCount} recipient{blastCount === 1 ? "" : "s"}
            </p>
          </div>
          <div className="space-y-2">
            <Label>Extra note (optional)</Label>
            <Textarea data-testid="blast-note-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Don't forget your white rose for formal…"
              className="bg-neutral-900 border-white/10" />
          </div>
          <Button data-testid="blast-send-button" onClick={sendBlast} disabled={sendingBlast || !eventId || blastCount === 0}
            className="w-full bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold gap-2">
            <Send className="w-4 h-4" /> {sendingBlast ? "Sending…" : "Send Reminder Email"}
          </Button>
        </Card>

        {/* Personal email */}
        <Card className="glass-card p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
            <Mail className="w-4 h-4 text-[var(--sn-gold)]" /> Personal Email
          </div>
          <div className="space-y-2">
            <Label>Members ({selected.length} selected)</Label>
            <div className="max-h-44 overflow-y-auto rounded-md border border-white/10 bg-black/30 p-2 space-y-0.5" data-testid="personal-recipient-list">
              {users.map(u => (
                <label key={u.id} data-testid={`personal-recipient-${u.id}`}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer text-sm text-neutral-300">
                  <input type="checkbox" checked={selected.includes(u.id)}
                    onChange={(e) => toggle(u.id, e.target.checked)}
                    className="accent-[#FFEC69]" />
                  {u.name} <span className="text-xs text-neutral-500">({u.email})</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea data-testid="personal-message-input" rows={5} value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message… (sent from you, with your name and position)"
              className="bg-neutral-900 border-white/10" />
            <div className="text-xs text-neutral-500 text-right">{message.length}/2000</div>
          </div>
          <Button data-testid="personal-send-button" onClick={sendPersonal} disabled={sendingPersonal || !selected.length || !message.trim()}
            className="w-full bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold gap-2">
            <Send className="w-4 h-4" /> {sendingPersonal ? "Sending…" : "Send Personal Email"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
